import { Component, EventEmitter, Output, ChangeDetectorRef, ViewChild, OnDestroy, OnInit, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgForm } from '@angular/forms';
import { Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { EfileAdminService } from '../efile-admin.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import {
  firstGridRowObjectFromPayload,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-efile-irs-submission',
    templateUrl: './efile-irs-submission.component.html',
    styleUrls: ['./efile-irs-submission.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class EfileIrsSubmissionComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  globalService: any = {};
  GENERAL_CONFIG: any = {};
  rowData: any = {};
  rowDataCopy: any = {};

  /** Parse "HH:mm:ss" or "HH:mm" to a Date (today with that time) for PrimeNG Calendar */
  private parseTimeToDate(timeStr: string): Date {
    if (!timeStr || !timeStr.trim()) return new Date();
    const parts = timeStr.trim().split(':').map((p, i) => parseInt(p, 10) || 0);
    const d = new Date();
    d.setHours(parts[0] ?? 0, parts[1] ?? 0, parts[2] ?? 0, 0);
    return d;
  }

  /** Sync time string from PrimeNG Calendar (Date) - 24h HH:mm:ss */
  onTimeSelect(): void {
    if (this.selectedTimeAsDate) {
      this.selectedTime = this.formatTimeForInput(this.selectedTimeAsDate);
    }
    this.cdr.detectChanges();
  }

  /** Native time input (HH:mm or HH:mm:ss) – incremental arrows, sync to selectedTime/selectedTimeAsDate */
  onTimeInputChange(value: string): void {
    if (value != null && value.trim() !== '') {
      this.selectedTime = value.length === 5 ? value + ':00' : value;
    }
    this.selectedTimeAsDate = this.parseTimeToDate(this.selectedTime);
    this.cdr.detectChanges();
  }

  title = 'Edit IRS Submission';
  minimise = false;
  saving = false;
  errorIndicator = false;
  errorAccepted = false;
  selectedDate = '';
  selectedTime = '';
  /** Time as Date for PrimeNG Calendar (timeOnly) - 24h format with HH/mm arrows */
  selectedTimeAsDate: Date | null = null;

  /** Only today's date is selectable for Created TS (no past or future dates). */
  get minDate(): string {
    return this.formatDateForInput(new Date());
  }
  get maxDate(): string {
    return this.formatDateForInput(new Date());
  }

  @ViewChild('irsForm') irsSubForm!: NgForm;

  constructor(
    private efileAdminService: EfileAdminService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext())
    );
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext())
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    const r = firstGridRowObjectFromPayload(rowPayloadFromActiveScreen(activeScreen));
    this.applyRowPayload(Object.keys(r).length ? r : null);
    this.cdr.markForCheck();
  }

  private applyRowPayload(r: Record<string, any> | null): void {
    try {
      if (r && Object.keys(r).length > 0) {
        this.rowData = { ...r };
        if (this.rowData.CREATED_TS) {
          this.rowData.CREATED_TS = String(this.rowData.CREATED_TS).replace(/\+00:00/g, '');
        }
        this.selectedDate = this.rowData.CREATED_TS
          ? this.formatDateForInput(new Date(this.rowData.CREATED_TS))
          : this.formatDateForInput(new Date());
        this.selectedTime = this.rowData.CREATED_TS
          ? this.formatTimeForInput(new Date(this.rowData.CREATED_TS))
          : this.formatTimeForInput(new Date());
      } else {
        this.rowData = {};
        this.selectedDate = this.formatDateForInput(new Date());
        this.selectedTime = this.formatTimeForInput(new Date());
      }
      this.selectedTimeAsDate = this.parseTimeToDate(this.selectedTime);
      this.rowDataCopy = r ? { ...r } : {};
    } catch (_) {
      this.rowData = {};
      this.rowDataCopy = {};
      this.selectedDate = this.formatDateForInput(new Date());
      this.selectedTime = this.formatTimeForInput(new Date());
      this.selectedTimeAsDate = this.parseTimeToDate(this.selectedTime);
    }
  }

  private formatDateForInput(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private formatTimeForInput(d: Date): string {
    const h = String(d.getHours()).padStart(2, '0');
    const m = String(d.getMinutes()).padStart(2, '0');
    const s = String(d.getSeconds()).padStart(2, '0');
    return `${h}:${m}:${s}`;
  }

  /**
   * Format created_ts for save payload - matches AngularJS:
   * selectedDate.toLocaleDateString() + ' ' + mytime.toLocaleTimeString()
   * e.g. "2/25/2026 8:28:30 PM"
   */
  private formatCreatedTsForPayload(): string {
    const datePart = this.selectedDate; // YYYY-MM-DD
    const timePart = this.selectedTimeAsDate
      ? this.formatTimeForInput(this.selectedTimeAsDate)
      : this.selectedTime; // HH:mm:ss
    if (!datePart || !timePart) return '';
    const [y, mo, d] = datePart.split('-').map(Number);
    const [h, m, s] = timePart.split(':').map(Number);
    const dt = new Date(y, mo - 1, d, h, m, s || 0);
    return dt.toLocaleDateString() + ' ' + dt.toLocaleTimeString();
  }

  validateIndicator(): void {
    const v = (this.rowData?.INDICATOR || '').toUpperCase();
    this.errorIndicator = v !== 'P' && v !== 'T';
    this.cdr.detectChanges();
  }

  validateAccepted(): void {
    const v = this.rowData?.ACCEPTED || '';
    this.errorAccepted = v !== 'Y' && v !== 'N';
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  save(): void {
    if (this.saving || this.errorIndicator || this.errorAccepted) return;
    if (this.irsSubForm?.invalid) {
      this.gtwSnackbarService.openSnackBar('Please fill all required fields.', 'gtw-snackbar--red');
      return;
    }

    // Payload parity with legacy EfileDataAdminController.js L679-687 —
    // all seven keys are sent on every save (the form binds both
    // ACKNOWLEDGEMENT and SUBMISSION_RECEIPT textareas, so neither can
    // be dropped on an edit). Keys with no value resolve to undefined
    // and are stripped by `JSON.stringify` — same end result as legacy
    // `vm.row.X` reads for an unset field.
    const createdTs = this.formatCreatedTsForPayload();
    this.rowData.CREATED_TS = createdTs;

    const data: Record<string, any> = {
      id: this.rowData.ID,
      submission_id: this.rowData.SUBMISSION_ID,
      indicator: (this.rowData.INDICATOR || '').toUpperCase(),
      accepted: (this.rowData.ACCEPTED || '').toUpperCase(),
      created_ts: createdTs,
      acknowledgement: this.rowData.ACKNOWLEDGEMENT,
      submission_receipt: this.rowData.SUBMISSION_RECEIPT,
    };

    const efileApiUrl =
      this.GENERAL_CONFIG?.gtw_efile_api_url ??
      this.GENERAL_CONFIG?.efile ??
      '';

    this.saving = true;
    this.cdr.detectChanges();

    this.efileAdminService.saveIrsSubmission(efileApiUrl, data).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.saving = false;
        this.cdr.detectChanges();
        if (res?.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar('Data saved successfully.', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          const errorMessage = (res?.errorMessage && res.errorMessage !== 'null') ? res.errorMessage : 'Error saving.';
          this.gtwSnackbarService.openSnackBar(errorMessage, 'gtw-snackbar--red');
        }
      },
      error: () => {
        this.saving = false;
        this.cdr.detectChanges();
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
      }
    });
  }
}
